// Shared HTML sanitizer + safe renderer helpers for CRM surfaces.
//
// Inquiry and lead records carry mixed content: the `messages[]` array has
// real Gmail HTML, while `responses[]` usually has plain text (but sometimes
// contains HTML fragments copied from templates). Rendering either one with
// raw `dangerouslySetInnerHTML` is unsafe and — worse — plain-text responses
// rendered that way show literal `<p>` / `<br/>` tags to operators.
//
// We route every piece of externally-sourced markup through a conservative
// DOMParser allowlist (no scripts, no event handlers, no style tags) and
// expose a small helper that:
//   1. Detects whether the input looks like HTML or plain text,
//   2. Converts plain text into `<br/>`-separated HTML so newlines render,
//   3. Returns sanitized HTML ready for `dangerouslySetInnerHTML`.
//
// Anything that needs to *display* user/guest-authored markup should go
// through `safeHtml` or the `<SafeHtml>` component below.

import React from "react";

const ALLOWED_TAGS = [
  "a",
  "b",
  "blockquote",
  "br",
  "code",
  "div",
  "em",
  "hr",
  "i",
  "img",
  "li",
  "ol",
  "p",
  "pre",
  "s",
  "small",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];

const ALLOWED_ATTR = new Set([
  "href",
  "src",
  "alt",
  "title",
  "colspan",
  "rowspan",
  "align",
  "width",
  "height",
]);

const URL_ATTRS = new Set(["href", "src"]);

function isSafeUrl(value) {
  if (!value) return true;
  const trimmed = String(value)
    .trim()
    .split("")
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code > 31 && code !== 127 && !/\s/.test(ch);
    })
    .join("");
  if (/^(javascript|vbscript|data):/i.test(trimmed)) return false;
  return true;
}

// Best-effort HTML detector: look for any real tag. We purposely avoid false
// positives on plain text that happens to contain angle brackets by also
// matching `<br>` / `<br/>` standalones that Gmail injects.
export function looksLikeHtml(value) {
  if (!value) return false;
  return /<\s*\/?[a-z][^>]*>/i.test(String(value));
}

// Convert plain text into minimal HTML: escape special chars, turn double
// newlines into paragraph breaks, single newlines into <br/>. URLs are NOT
// auto-linked here to keep the transform reversible and predictable.
function plainTextToSafeHtml(text) {
  const escaped = String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  const paragraphs = escaped.split(/\n{2,}/).map((p) => p.replace(/\n/g, "<br/>"));
  return paragraphs.map((p) => `<p>${p}</p>`).join("");
}

// Normalize any input (HTML string or plain text) into sanitized HTML.
export function safeHtml(input, { assumeHtml = null } = {}) {
  if (input == null) return "";
  const str = String(input);
  if (!str.trim()) return "";

  const isHtml = assumeHtml === null ? looksLikeHtml(str) : Boolean(assumeHtml);
  const raw = isHtml ? str : plainTextToSafeHtml(str);
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return plainTextToSafeHtml(htmlToPreview(raw, { maxLength: 10_000 }));
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<body>${raw}</body>`, "text/html");

  const sanitizeNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return document.createTextNode(node.textContent || "");
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return document.createTextNode("");
    }

    const tag = node.tagName.toLowerCase();
    if (!ALLOWED_TAGS.includes(tag)) {
      const fragment = document.createDocumentFragment();
      node.childNodes.forEach((child) => fragment.appendChild(sanitizeNode(child)));
      return fragment;
    }

    const clean = document.createElement(tag);
    Array.from(node.attributes || []).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value || "";
      if (!ALLOWED_ATTR.has(name)) return;
      if (URL_ATTRS.has(name) && !isSafeUrl(value)) return;
      clean.setAttribute(name, value);
    });

    if (tag === "a") {
      clean.setAttribute("target", "_blank");
      clean.setAttribute("rel", "noopener noreferrer");
    }

    node.childNodes.forEach((child) => clean.appendChild(sanitizeNode(child)));
    return clean;
  };

  const container = document.createElement("div");
  doc.body.childNodes.forEach((node) => container.appendChild(sanitizeNode(node)));
  return container.innerHTML;
}

// Drop-in renderer for sanitized HTML blocks.
export function SafeHtml({ value, assumeHtml = null, className = "", ...rest }) {
  const html = safeHtml(value, { assumeHtml });
  return (
    <div
      {...rest}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// Utility: collapse HTML to a short preview string (no tags, no newlines).
// Useful for list rows where we only want a single line of context.
export function htmlToPreview(input, { maxLength = 160 } = {}) {
  if (!input) return "";
  const stripped = String(input)
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
  if (stripped.length <= maxLength) return stripped;
  return `${stripped.slice(0, maxLength - 1).trim()}…`;
}

export default safeHtml;
